import * as logger from 'loglevel';
import { getDateRangeAsDates } from '../../helpers/localization/localizationUtils';
import { getUuidMin } from '@front10/helpers/dist/uuid';

import axiosWrapper from '../../helpers/axiosWrapper';
import { NO_DURATION_DEFINED } from '../../helpers/axiosWrapper/cache';
import transformRequestGroupRoutes from './transformRequest/transformSmartFilter';

/**
 * Makes sure the fares response is complain with a basic schema
 * @param {*} fares
 */
const validateFaresResponse = (fares) => {
  const validation = { isValid: true, message: '' };
  //TODO: This should be removed in future versions, it was added to advance the development of the component,
  //      the issue is caused by the backend which sometimes returns incorrect outputs
  if (typeof fares === 'object') {
    return validation;
  }
  if (!fares || !Array.isArray(fares)) {
    validation.isValid = false;
    return {
      isValid: false,
      data: fares,
      message: 'INTEGRATION__FARES: Fares must be an Array'
    };
  }
  return validation;
};

const journeyTypeMap = {
  ROUND_TRIP: 'rt',
  ONE_WAY: 'ow',
  MULTI_CITY: 'mc',
  EACH_WAY: 'ew'
};

const mapTravelClassData = (travelClassData, classType) => {
  return travelClassData.map((fare) => ({
    farenetTravelClass: classType,
    currencyCode: fare.priceSpecification.currencyCode,
    formattedTotalPrice: fare.priceSpecification.formattedTotalPrice,
    totalPrice: fare.priceSpecification.totalPrice,
    usdTotalPrice: fare.priceSpecification.usdTotalPrice,
    flightDeltaDays: fare.flightDeltaDays,
    shortenedFormattedTotalPrice: fare.priceSpecification.shortenedFormattedTotalPrice,
    redemption: fare.priceSpecification.redemption || undefined
  }));
};

/**
 * The API returns the data in non linear format
 * so this function will intend normalize the data for the components
 * @returns
 */
const transformData = (faresRawData) => {
  //TODO: This should be removed in future versions, it was added to advance the development of the component,
  //      the issue is caused by the backend which sometimes returns incorrect outputs
  if (!Array.isArray(faresRawData)) {
    faresRawData = [faresRawData];
  }

  const fares = faresRawData
    .map((fare) => {
      let searchDate =
        fare.ECONOMY?.[0]?.searchDate ||
        fare.PREMIUMECONOMY?.[0]?.searchDate ||
        fare.BUSINESS?.[0]?.searchDate ||
        fare.FIRST?.[0]?.searchDate;
      const journeyType =
        journeyTypeMap[
          fare.ECONOMY?.[0]?.journeyType ||
            fare.PREMIUMECONOMY?.[0]?.journeyType ||
            fare.BUSINESS?.[0]?.journeyType ||
            fare.FIRST?.[0]?.journeyType
        ];

      // Workaround for Safari and IE
      if (searchDate && /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d+[+|-]\d{4}/.test(searchDate)) {
        searchDate = `${searchDate.substring(0, searchDate.length - 2)}:${searchDate.substring(
          searchDate.length - 2
        )}`;
      }

      const newFare = {
        origin: fare.origin,
        originCity: fare.originHierarchy.city.name,
        destination: fare.destination,
        destinationCity: fare.destinationHierarchy.city.name,
        departureDate: fare.departureDate,
        returnDate: fare.returnDate,
        searchDate: searchDate,
        formattedDepartureDate: fare.departureDate,
        formattedReturnDate: fare.returnDate,
        journeyType: journeyType,
        index: getUuidMin(
          `${fare.origin}${fare.destination}${fare.departureDate}${fare.returnDate}${fare.originHierarchy.city.name}${fare.destinationHierarchy.city.name}${fare.departureDate}${fare.returnDate}${searchDate}${journeyType}`
        ),
        travelClasses: []
      };

      if (fare.ECONOMY) {
        newFare.travelClasses.push(...mapTravelClassData(fare.ECONOMY, 'ECONOMY'));
      }

      if (fare.PREMIUMECONOMY) {
        newFare.travelClasses.push(...mapTravelClassData(fare.PREMIUMECONOMY, 'PREMIUM_ECONOMY'));
      }

      if (fare.BUSINESS) {
        newFare.travelClasses.push(...mapTravelClassData(fare.BUSINESS, 'BUSINESS'));
      }

      if (fare.FIRST) {
        newFare.travelClasses.push(...mapTravelClassData(fare.FIRST, 'FIRST'));
      }

      return newFare;
    })
    .reduce((a, b) => a.concat(b), []);
  return fares;
};

/**
 * Retrieve fares from the Endpoint
 * @param {*} filter
 * @param {*} integrationSettings
 */
const getFares = (filter, integrationSettings, withAgreggations, meta, pageContext) => {
  return new Promise((resolve, reject) => {
    if (!integrationSettings) {
      const MESSAGE = `INTEGRATION__FARES: **integrationSettings** not especified.`;
      logger.error(MESSAGE);
      reject(MESSAGE);
    }
    const {
      v1FaresTravelClasses: faresApiSettings,
      v1FaresTravelClasses: { cache = false, cacheDuration = NO_DURATION_DEFINED }
    } = integrationSettings;
    const filterCopy = { ...filter };

    if (
      !faresApiSettings ||
      !faresApiSettings.method ||
      !faresApiSettings.endPoint ||
      !faresApiSettings.headers
    ) {
      const MESSAGE = `INTEGRATION__FARES: **integrationSettings.v1FaresTravelClasses** configuration issues.`;
      logger.error(MESSAGE);
      reject(MESSAGE);
    }

    filterCopy.departure = getDateRangeAsDates(
      filterCopy.departure,
      'YYYY-MM-DD',
      'INTEGRATION__FARES'
    );
    if (filterCopy.return) {
      filterCopy.return = getDateRangeAsDates(
        filterCopy.return,
        'YYYY-MM-DD',
        'INTEGRATION__FARES'
      );
    }

    // params to delete don't matter the value store
    const paramsDelete = [
      'languageCode',
      'airlineIataCode',
      'siteEdition',
      'faresLimit',
      'page',
      'airport',
      'travelRestrictionsSource',
      'dataExpirationWindow',
      'datePattern',
      'faresPerRoute',
      'outputFields',
      'passengers',
      'priceFormat',
      'flightType',
      'journeyType'
    ];

    axiosWrapper(
      {
        method: faresApiSettings.method,
        url: `${faresApiSettings.endPoint}`,
        headers: { ...faresApiSettings.headers },
        data: transformRequestGroupRoutes(filterCopy, paramsDelete)
      },
      {
        resource: 'INTEGRATION__FARES__TRAVELCLASSES',
        meta,
        integrationSettings,
        pageContext,
        cache,
        cacheDuration
      }
    )
      .then((response) => {
        const validation = validateFaresResponse(response.data);
        if (validation.isValid) {
          logger.info('INTEGRATION__FARES: Successful request', response.data);
          const fares = transformData(response.data);
          resolve(fares);
        } else {
          logger.error(validation.message, validation.data);
          reject(response.data);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};

export { getFares, transformData };
